import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import memoize from 'memoize-one';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Toast} from 'primereact/toast';

import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {panelSchema} from '~/shared/src/validationSchemes/panel';
import {overviewSchema} from '~/shared/src/validationSchemes/modules/overview';
import {SaveDataResult} from '~/shared/src/utilities/modules/data';
import {
    ClearConfigParameters,
    GetViewKeyByNameResult,
    LoadConfigResult
} from '~/shared/src/utilities/modules/overview';
import objectUtils from '~/shared/src/utilities/objectUtils';

import Overview, {
    defaultProps as overviewDefaultProps,
    Props as OverviewProps,
    ComponentProps as OverviewComponentProps,
    componentProps as overviewComponentProps
} from '~/frontend/src/components/base/Overview/index';
import {preInitialize} from '~/frontend/src/components/base/Overview/utilities';
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {isDataModified} from '~/frontend/src/utilities/dataUtils';
import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';

import * as PanelManagerActions from '~/frontend/src/actions/panelManager';
import {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import {SetPanelInvalid} from '~/frontend/src/actions/panelManager/setPanelInvalid';
import {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import closePanel from '~/frontend/src/actions/panelManager/closePanel';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import saveData from '~/frontend/src/actions/panel/dataTable/saveData';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    setElusiveData,
    SetElusiveDataAction
} from '~/frontend/src/actions/elusiveData';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import {
    MsgParams,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import cancelRequests from '~/frontend/src/actions/ajax/cancelRequests';


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & { showCouldNotSavePopup? : boolean };


const Actions = Object.assign( {}, PanelManagerActions, PanelActions, ElusiveDataActions, IntercomActions ) as any;
type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;


const defaultProps = {
    ...overviewDefaultProps
};

type DefaultProps = typeof defaultProps;

type Props = OverviewProps & {

    /**
     * The name of the view
     */
    viewName? : string;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    title? : string;

    /**
     * The title for the tab of the panel. If not set the title will be computed.
     */
    tabTitle? : string;

    /**
     * The title for the tab tooltip of the panel. If not set the title will be computed.
     */
    tooltipTitle? : string;

    /**
     * The icon to display the panel with. If not set the icon will be computed.
     */
    iconCls? : string;
};

export type ComponentProps = Partial<DefaultProps> & Props;
export const componentProps = keys<ComponentProps>();

type StateProps = {

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionProps = {
    // panel manager actions
    setPanelDirty : SetPanelDirty;
    setPanelInvalid : SetPanelInvalid;
    setPanelInfo : SetPanelInfo;

    // common panel actions
    setParams : SetParams;

    // elusiveData actions
    setElusiveData : SetElusiveData;

    // intercom actions
    unsetMessage : UnsetMessage;
};

type AllProps = DefaultProps & Props & StateProps & ActionProps;


type State = {
    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | JSX.Element | ( string | JSX.Element )[];
    errorMessage? : string | JSX.Element | ( string | JSX.Element )[];
};


/**
 * OverviewView
 */
class OverviewView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /**
     * YOONITE lifecycle method
     */
    static onPreInitializePanel( props : ComponentProps & {
        user : User
    } ) : ComponentProps {
        if ( !props.schema && props.user?.application?.schema ) {
            props.schema = props.user.application.schema;
        }

        return preInitialize( {
            ...props
        } as OverviewComponentProps & { user : User } );
    }

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps ) {
        // backend request to clear cached overview configuration data
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const params : ClearConfigParameters = {
                configHash: props.configHash
            };

            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            delete global.data[ props.dataContext ];

            await xhr.post( `${BASE_URL}/api/overview/clearConfig`, params, undefined, props.uid, props.uid, dispatch );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            await OverviewView.savePanelData(
                dispatch,
                getState,
                props
            )
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {

            const result = await OverviewView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid,
                  schema
              }                                        = propsWithDefaults,
              errorData                                = getState()?.elusiveData?.[ uid ]?.errorData as ErrorData | undefined;

        // panel has errors -> show popup
        if ( errorData?.count ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );

            return Promise.resolve( false );
        }

        // panel has no errors -> save
        else {
            return dispatch( saveData(
                uid,
                schema,
                successMsgParams
            ) );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'viewKey' | 'viewName' | 'search'> ) : string {
        let retVal = `#${OverviewView.panelType}/${props.viewName ?? props.viewKey}`;

        if ( props.search ) {
            retVal += '?search';
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              props                            = parts?.[ 3 ]?.split( '&' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        // params[ 0 ] = panelType

        // params[ 1 ] = viewName
        if ( params?.[ 1 ] ) {
            const viewName = params[ 1 ];

            // resolve viewKey
            const res    = await xhr.post(
                      `${BASE_URL}/api/overview/getViewKeyByName`,
                      {
                          viewName
                      }
                  ),
                  result = res?.data as GetViewKeyByNameResult;

            if ( result ) {
                if ( result.data.viewKey ) {
                    retVal.viewKey = result.data.viewKey;
                }
                else {
                    stackError.add( new LocalizableError( 'YN.base.frontend.overview.resolvePanelPropsFromURL.error.couldNotResolveViewKey', { name: viewName } ) );
                }
            }
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.frontend.overview.resolvePanelPropsFromURL.error.couldNotResolveViewName' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        for ( const prop of props ) {
            const propParts = prop.split( '=' );

            if ( propParts[ 0 ] === 'search' ) {
                retVal.search = true;
            }
        }

        return retVal;
    }

    // panel manager config
    static panelType = 'Overview' as string;
    static panelTabTitle = 'YN.base.frontend.overview.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:Overview' as string;

    static panelPropsSchemaMsgIdPrefix = 'YN.base.overview';
    static panelPropsSchema = Joi.object()
                                 .concat( panelSchema )
                                 .concat( Joi.object( {
                                     // panelSchema defaults
                                     panelType    : Joi.string().valid( OverviewView.panelType ),
                                     panelTabTitle: Joi.string().default( OverviewView.panelTabTitle ),
                                     panelTabIcon : Joi.string().default( OverviewView.panelTabIcon )
                                 } ) )
                                 .concat( requestBaseParametersSchema )
                                 .concat( overviewSchema )
                                 .concat( Joi.object( {
                                     viewKey: Joi.string()
                                                 .required()
                                 } ) );


    /* properties *************************************************************************************************************************/

    toast : Toast | null = null;
    toastTopOffset : number = 55;
    toastBottomOffset : number = 32;

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            isDirty   : false,
            errorCount: 0
        };

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.setPanelInfo = memoize( this.setPanelInfo );
        this.buildPanelPathFromProps = memoize( this.buildPanelPathFromProps );

        this.onOverviewConfigLoad = this.onOverviewConfigLoad.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  dataContext,
                  schema,
                  elusiveData
              }                       = props,
              retVal : Partial<State> = {
                  isDirty     : isDataModified( dataContext, schema ),
                  errorCount  : 0,
                  errorTooltip: undefined,
                  errorMessage: undefined
              };

        // errors
        if ( elusiveData?.errorData ) {
            const errorData = elusiveData.errorData;

            retVal.errorCount = errorData.count;

            retVal.errorTooltip = <div key="error" className="yn-tooltip-error">
                <div className="yn-tooltip-header">
                    {__( 'YN.base.frontend.dataTable.tooltip.error.title' )}
                </div>
                {errorData.tooltip}
            </div>;

            retVal.errorMessage = <>
                <div className="yn-error-message">
                    {__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.text' )}
                </div>
                <hr className="yn-error-ruler"/>
                <div className="yn-error-suberror">
                    {errorData.message}
                </div>
            </>;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.afterRender();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : AllProps ) {
        this.afterRender();
    }

    /**
     * REDUX stuff
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        const changedProps = objectUtils.getDiffKeys(
                  this.props,
                  nextProps,
                  [
                      'actionColumns',
                      'cellEditorErrors',
                      'columns',
                      'columnDefaults',
                      'columnDefinitions',
                      'dataStructure',
                      'elusiveData',
                      'elusiveData.cellEditorErrors',
                      'elusiveData.cellEditorInitialValues',
                      'elusiveData.cellEditorValues',
                      'elusiveData.columnsCache',
                      'elusiveData.data',
                      'elusiveData.editCell',
                      'elusiveData.lastParams',
                      'elusiveData.metaData',
                      'filters',
                      'fixFilters',
                      'footer',
                      'rowClass',
                      'rowExpansionTemplate',
                      'rowGroupHeaderTemplate',
                      'rowGroupFooterTemplate',
                      'rowState',
                      'rowStyling',
                      'searchColumns',
                      'sorters',
                      'user',
                      'xhrRequestsForComponent',
                      //
                      'intercomMessages'
                  ],
                  [ 'elusiveData' ]
              ),
              stateChanged = this.state !== nextState;

        let retVal : boolean = false;

        if ( this.state.isDirty !== nextState.isDirty ) {
            this.props.setPanelDirty( this.props.uid, nextState.isDirty );
        }

        if ( this.state.errorCount !== nextState.errorCount ) {
            this.props.setPanelInvalid( this.props.uid, !!nextState.errorCount );
        }

        if (
            stateChanged
            || changedProps.length === 1
            || changedProps.length > 2
            || ( changedProps.length === 2 && !changedProps.includes( 'elusiveData.lastParams' ) && !changedProps.includes( 'elusiveData.columnsCache' ) )
        ) {
            retVal = true;
        }

        return retVal;
    }


    /* event handlers *********************************************************************************************************************/

    onOverviewConfigLoad( config : LoadConfigResult ) {
        this.props.setParams( this.props.uid, {
            viewName    : config.data.viewName,
            title       : config.data.title as string,
            tabTitle    : config.data.tabTitle as string,
            tooltipTitle: config.data.tooltipTitle as string,
            iconCls     : config.data.iconCls
        } );
    }


    /* private ****************************************************************************************************************************/

    buildPanelPathFromProps( viewKey : AllProps['viewKey'], viewName : AllProps['viewName'], search : AllProps['search'] ) {
        return OverviewView.buildPanelPathFromProps( { viewKey, viewName, search } )
    }

    afterRender() {
        const {
                  uid,
                  search,
                  viewKey,
                  viewName,
                  iconCls,
                  tabTitle: tabTitleOrig,
                  tooltipTitle,
                  intercomMessages
              }                     = this.props,
              {
                  isDirty,
                  errorCount
              }                     = this.state,
              toolbar               = document.getElementById( ( `${uid}_toolbar` ) ) as HTMLElement,
              toolbarHeight         = toolbar?.offsetHeight ?? 0,
              dataTable             = document.getElementById( `${uid}_datatable` ) as HTMLElement,
              dataTableHeader       = dataTable
                  ? dataTable.querySelector( '.p-datatable-thead' ) as HTMLElement
                  : null,
              dataTableHeaderHeight = dataTableHeader?.offsetHeight ?? 0,
              toastTopOffset        = toolbarHeight + dataTableHeaderHeight,
              dataTablePagination   = dataTable
                  ? dataTable.querySelector( '.p-paginator' ) as HTMLElement
                  : null,
              toastBottomOffset     = dataTablePagination?.offsetHeight ?? 0,
              icon                  = this.getPanelIcon( OverviewView.panelTabIcon, iconCls ),
              tabTitle              = tabTitleOrig ?? ( metaModel[ 'CFG' ].getItemType( 'Overview' )?.displayAttribute === 'key' ? viewKey : viewName ),
              title                 = this.getPanelTitle( OverviewView.panelTabTitle, tabTitle ),
              path                  = this.buildPanelPathFromProps( viewKey, viewName, search );

        if ( toastTopOffset ) {
            this.toastTopOffset = toastTopOffset;
        }
        if ( toastBottomOffset ) {
            this.toastBottomOffset = toastBottomOffset;
        }

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, tooltipTitle, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );
            for ( const key of toastKeys ) {
                // show toast
                if ( key === 'configChanged' && intercomMessages.toast[ key ].id === 'showWarn' ) {
                    this.toast.show( {
                        severity: 'warn',
                        sticky  : true,
                        summary : __( 'YN.base.frontend.overview.setView.configChanged.title' ),
                        detail  : __( 'YN.base.frontend.overview.setView.configChanged.text' )
                    } );
                }

                else if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage( this.props.uid, 'toast', key as string );
            }
        }
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  elusiveData
              }             = this.props,
              {
                  errorMessage
              }             = this.state,
              overviewProps = {
                  ..._.pick( this.props, overviewComponentProps ) as OverviewComponentProps,

                  key: `${uid}_overview`,

                  onConfigLoad: this.onOverviewConfigLoad
              };

        return <>
            <Dialog
                key={`${uid}_dialog`}
                header={__( 'YN.base.frontend.dataTable.couldNotSaveWhileErrors.title' )}
                footer={<div><Button label="OK" className="p-button-outlined" onClick={() => this.props.setElusiveData( this.props.uid, { showCouldNotSavePopup: false } )}/></div>}
                maskClassName="yn-dialog-panel-modal"
                appendTo="self"
                position="top"
                visible={elusiveData?.showCouldNotSavePopup}
                style={{ width: 'auto', maxWidth: '80%' }}
                closable={false}
                className="yn-error-dialog"
                onHide={() => {
                }}>
                {errorMessage}
            </Dialog>
            <div style={{ position: 'relative' }} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute', marginTop: this.toastTopOffset, marginBottom: this.toastBottomOffset }}
                />
                <Overview
                    {...overviewProps}
                />
            </div>
        </>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        intercomMessages: undefined,
        elusiveData     : undefined,
        user            : undefined
    };

    // get intercom messages
    if ( state.intercom?.[ ownProps.uid ] ) {
        retVal.intercomMessages = state.intercom[ ownProps.uid ];
    }

    // get elusiveData
    // for the data (items and references) belonging to this component
    if ( state.elusiveData?.[ ownProps.dataContext ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.dataContext ]
        };
    }
    // for all other information belonging to this component
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.uid ]
        };
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( OverviewView );

